import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { Check, Loader2, Mail, Clock, ShieldCheck, ShieldAlert, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitContactMessage } from "@/lib/contact.functions";
import { Layout } from "@/components/site/Layout";

const TITLE = "Contact the Desk | XAU Pro Academy";
const DESC =
  "Reach the XAU Pro Academy desk for technical support, account and membership questions, or partnership inquiries. Support coverage Monday to Friday.";

const TOPICS = [
  "Technical support",
  "Account and membership",
  "Platform and methodology",
  "Partnerships and media",
  "Other",
] as const;

const formSchema = z.object({
  name: z.string().trim().min(2, "Enter your name").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  topic: z.enum(TOPICS),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000),
});

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://xauproacademy.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://xauproacademy.com/contact" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: TITLE,
          description: DESC,
          url: "https://xauproacademy.com/contact",
          mainEntity: {
            "@type": "Organization",
            name: "XAU Pro Academy",
            legalName: "Orion AI Technologies",
            url: "https://xauproacademy.com/",
            email: "support@xauproacademy.com",
            identifier: { "@type": "PropertyValue", name: "License No.", value: "58070" },
            sameAs: ["https://x.com/XAUProAcademy", "https://www.youtube.com/@XAUProAcademy"],
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "support@xauproacademy.com",
              availableLanguage: ["English"],
              hoursAvailable: {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              },
            },
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<string>("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse({ name, email, topic, message });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Check the form fields.");
      return;
    }
    setBusy(true);
    const res = await submitContactMessage({
      data: { ...parsed.data, website },
    }).catch(() => ({ ok: false }));
    setBusy(false);
    if (!res.ok) {
      toast.error("Could not send your message. Please email support@xauproacademy.com directly.");
      return;
    }
    setDone(true);
  };

  return (
    <Layout>
    <div className="container mx-auto max-w-5xl px-4 py-16">
      <header className="max-w-2xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Contact Desk</p>
        <h1 className="mt-3 font-display text-3xl font-bold md:text-4xl">Contact the desk</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          For technical support, account and membership questions, or partnership inquiries.
          Messages are reviewed by the desk during support hours.
        </p>
      </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-xl border border-border/60 bg-card/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="h-4 w-4 text-gold" /> Direct email
            </div>
            <a
              href="mailto:support@xauproacademy.com"
              className="mt-2 block text-sm text-muted-foreground hover:text-gold"
            >
              support@xauproacademy.com
            </a>
          </div>
          <div className="rounded-xl border border-border/60 bg-card/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Clock className="h-4 w-4 text-gold" /> Support coverage
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Monday to Friday</p>
            <p className="mt-1 text-xs text-muted-foreground">Replies typically within 24 business hours.</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Building2 className="h-4 w-4 text-gold" /> Operating entity
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Orion AI Technologies</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">License No. 58070</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 text-gold" /> Scope of support
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              The desk handles platform, account, and technical matters. Support channels do not
              provide personalized investment advice or trade recommendations. Common questions are
              answered in the <Link to="/faq" className="text-foreground underline underline-offset-2">FAQ</Link>.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card/60 p-6 lg:col-span-3">
          {done ? (
            <div className="flex h-full min-h-64 flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold-muted">
                <Check className="h-6 w-6 text-gold" />
              </div>
              <p className="font-display text-lg font-semibold">Message received</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Your message has been relayed to the desk. Expect a reply during support hours.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ct-name">Name</Label>
                  <Input
                    id="ct-name"
                    required
                    maxLength={100}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ct-email">Email</Label>
                  <Input
                    id="ct-email"
                    type="email"
                    required
                    maxLength={255}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-topic">Topic</Label>
                <Select value={topic} onValueChange={setTopic}>
                  <SelectTrigger id="ct-topic">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOPICS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ct-message">Message</Label>
                <Textarea
                  id="ct-message"
                  required
                  maxLength={2000}
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can the desk help?"
                />
              </div>
              {/* Honeypot: hidden from humans, filled by bots */}
              <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="ct-website">Website</label>
                <input
                  id="ct-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <Button type="submit" variant="gold" className="w-full" disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Messages are relayed directly to the desk. No marketing sequence.
              </p>
            </form>
          )}
        </div>
      </div>

      <aside className="mt-12 flex gap-4 border-t border-border/50 pt-8">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />
        <div className="max-w-3xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
            Official channels
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            The desk communicates only through support@xauproacademy.com and @XAUProAcademy on X.
            We never send unsolicited direct messages, request deposits or crypto transfers, or offer
            account management. Treat any such contact as impersonation.
          </p>
        </div>
      </aside>
    </div>
    </Layout>
  );
}
